import {
  RecordResponse,
  AttributesResponse,
  ViewsResponse,
  MrlResponse,
  TypeResponse,
  TimelineResponse,
  ChipsResponse,
} from './schemas'
import type { FilterExpr, SortKeyWire, ViewPatch, DaimonInvoke, DaimonOutcome, DaimonCapability, DaimonInvocation, ProposedChangeData } from './schemas'
import type { FilterClause } from '../views/contract'
// Type-only import/re-export preserves callers without adding a runtime
// dependency on the renderer engine imported by views/period.ts.
import type { MasterPeriod } from '../views/period'
export type { MasterPeriod } from '../views/period'

import { authedFetch, fetchV1, readV1, readV1WithMeta, streamV1Lines } from './http'
import { PlenumApiError, unwrapV1, unwrapV1WithMeta } from './envelope'
import type { RecordValuesChunk } from './v1/records_values_chunk.gen'

/** One warning from the calculated-batch envelope (server Warning shape). */
export type CalcBatchWarning = { kind: string; message: string; details?: Record<string, unknown> }

/** A values answer as far as it has arrived (SEN-443: records/values streams
 *  per column). `complete` names the value keys already answered — a column
 *  in it with no value for a record has none ("—", its warning says why); a
 *  column not in it is still being computed. `done`: every column is in. */
export type ValuesAnswer<T = unknown> = {
  values: Record<string, Record<string, T>>
  warnings: CalcBatchWarning[]
  /** absent: every column (a whole answer) */
  complete?: string[]
  /** absent: a whole answer (done) */
  done?: boolean
}

function calcWarnings(raw: unknown): CalcBatchWarning[] {
  return Array.isArray(raw)
    ? raw.filter((w): w is CalcBatchWarning =>
        typeof w === 'object' && w !== null &&
        typeof (w as CalcBatchWarning).kind === 'string' &&
        typeof (w as CalcBatchWarning).message === 'string')
    : []
}
import { SubtypesResponse } from './schemaTypes'
import type { TypeCreateBody, TypeUpdateBody, AttributeCreateBody, AttributeUpdateBody, Subtype } from './schemaTypes'

/** records/query `group_by` row (monad/plenum/v1/data.py RecordGroup);
 *  `aggregates` in request order (AggregateValue), present when asked. */
export interface RecordGroup {
  key: Array<string | null>
  kennings?: Array<string | null>
  count: number
  aggregates?: Array<{ fn: string; attribute: string; value: number | null }>
  /** With `chips`: per key, the reference's chip icon. */
  icons?: Array<string | null> | null
}
/** One records/query aggregate (monad/plenum/v1/requests.py RecordAggregate). */
export interface RecordAggregate { fn: 'sum' | 'avg' | 'min' | 'max' | 'count'; attribute: string }
/** A widget's records binding — `Widget.bind.records` (monad/plenum/v1/requests.py
 *  RecordsBind: RecordsQuery's `filter`, `group_by` and `aggregates`, taken from
 *  that model, never redeclared). Attribute paths are slugs. */
export interface RecordsBind { filter?: FilterClause | null; group_by?: string[] | null; aggregates?: RecordAggregate[] | null }
/** `Widget.bind` (monad/plenum/v1/requests.py WidgetBind). */
export interface WidgetBind { records: RecordsBind }
/** The records question a widget asks: its binding, the host's composed filter, the window. */
export type RecordsQuestion = RecordsBind & { limit?: number; period_start?: string; period_end?: string }
/** A record-page layout (monad/plenum/v1/data.py RecordPageLayout; the element
 *  wire is monad/record_layouts.py): a FLAT element list linked by
 *  `parent_element_id`, ordered by `sort_order`. Element types: `record-page`,
 *  `overview-tab` (slug), `value-widget` (path; a leaf `format`), and —
 *  record-layout widgets, 2026-10-23 — `widget` (one dashboard widget bound to
 *  the open record), `section` (a registered record section, by slug) and
 *  `group` (a captioned container: the facts). */
export interface RecordPageLayout {
  layout_id: string
  layout_type: string
  title: string
  is_default: boolean
  elements: RecordLayoutElement[]
}
export interface RecordLayoutElement {
  id: string
  parent_element_id: string | null
  type: string
  sort_order: string
  slug?: string | null
  title?: string | null
  /** a group's display: 'rows' (property rows) or 'figures' (KPI cards) */
  display?: 'rows' | 'figures'
  path?: Array<{ type_id: string; attribute_id: string; format?: string | null }>
  widget?: DashboardWidget
}
/** A comment mention: a typed ref, one shape (monad/notifications.py MENTION_TYPES). */
export interface CommentMention { type: 'person'; id: string }
/** monad/plenum/v1/data.py CommentData. */
export interface CommentData {
  comment_id: string
  record_id: string
  parent_id: string | null
  body: string
  mentions: CommentMention[]
  created_at: string
  /** The author as an actor ref ({type, id}); names resolve through person.member. */
  created_by: { type?: string; id?: string } | null
  resolved_at: string | null
  archived_at: string | null
}

export class HttpError extends Error {
  /** `detail` is the human message; `payload` keeps the raw `detail` body when the
   *  backend returned a structured object (e.g. {code:'slug_taken', suggestions}). */
  constructor(public readonly status: number, public readonly detail: string, public readonly payload?: unknown) {
    super(detail)
    this.name = 'HttpError'
  }
}

/** Readiness manifest — the exact shape of monad ProvisionManifest.to_dict()
 *  (GET /tenants/{slug}/readiness, POST /tenants/provision, POST /auth/signup). */
export type StageStatus = 'ok' | 'skipped' | 'missing' | 'mismatch' | 'error'
export interface ReadinessStage { status: StageStatus; detail: unknown }
export interface ReadinessManifest {
  tenant_id: string | null
  slug: string
  created: boolean
  ready: boolean
  pack: string
  stages: Record<string, ReadinessStage>
}

async function getJson(path: string): Promise<unknown> {
  const res = await authedFetch(path, { headers: { 'Content-Type': 'application/json' } })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${path}`)
  return res.json()
}

/** A JSON write for fetchV1 (method + body). */
function v1Write(method: 'POST' | 'PATCH', body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
}

/** A verb as listed by GET /tenants/{slug}/verbs.
 *
 *  `description` is on the wire and the ⌘K palette searches it — the narrower
 *  earlier type simply dropped it. Phase and policy are deliberately NOT
 *  modelled here: the server owns both (PhaseRejection → 409, PolicyDenied →
 *  403), and a client-side copy of that ladder would be a second opinion able
 *  to disagree with the authority. */
export interface VerbSummary {
  verb_id: string
  slug: string
  name: string
  is_system: boolean
  description?: string | null
  /** The JSON Schema of the verb's parameters: what an action form asks for (served). */
  parameter_schema?: { required?: string[]; properties?: Record<string, unknown> } | null
  /** What the verb acts ON (served): the record type, and the parameter that takes the selected record
   *  id(s) — null for a collection-level action (an invite on the members list). Surfaces offer a verb by
   *  this, never by its slug. */
  subject?: { type_slug: string; parameter: string | null } | null
}

/** A verb's served summary of what an invocation WOULD do (dry run): facts and sample names, computed
 *  server-side and rendered as served. */
export interface VerbDryRunSummary {
  facts?: Array<{ label: string; value: string | number | null; reason?: string | null }>
  samples?: Array<{ label: string; items: string[] }>
}

/** An invocation's outcome as served (VerbInvocationResult): the pipeline's status, the change queued for a
 *  person, a dry run's summary, and a one-time hand-off (an invite's accept link, shown once, never stored). */
export interface VerbInvocation {
  status: string
  proposed_change_id: string | null
  dry_run_summary?: VerbDryRunSummary | null
  handoff?: { accept_url: string } | null
}

/** A type in a tenant — for the type switcher (types-as-data). */
export interface TypeInfo {
  type_id: string
  slug: string
  name: string
  /** Plural display name (e.g. "Sites"). Present when the backend sends
   *  plural_noun on GET /v1/tenants/{slug}/objects — falls back to name + "s". */
  pluralName: string
  /** Icon metadata: "lucide:<PascalName>" or null. Rendered by TypeIcon with letter-avatar fallback. */
  icon: string | null
  /** Substrate self-description types (subtype/tenant/tenant_member). They live
   *  under Configure, never the Records rail (CEO 2026-08-19) — metadata-driven,
   *  no slug list. Carried from the wire; the mapper previously DROPPED it,
   *  which silently defeated any is_system filter downstream. Optional:
   *  absent means "not a system type" (test fixtures, older callers). */
  is_system?: boolean
  /** SEN-1434 (CEO decision 4): left-rail placement declared by type metadata —
   *  'records' (default) | 'connections' (the Connections section) |
   *  'reference' (Reference data) | 'none' (rendered nowhere). Metadata-driven,
   *  no slug lists. Optional: absent means 'records' (test fixtures, older callers). */
  rail?: 'records' | 'connections' | 'reference' | 'none'
  /** SEN-24: declared order within the rail; zero is undeclared. */
  rail_order?: number
  /** SEN-75: live non-archived count carried by the type definition. */
  recordCount?: number
  /** When a record of the type was last written (a cell's creation); null: never. Only asked for
   *  (`listTypes(slug, { lastWrite: true })`) by the reference-data directory, and only reference types carry it. */
  lastWriteAt?: string | null
  /** Files and Skills (SEN-449/450): the type is listed as a DIRECTORY (`display.directory`): which of its
   *  attributes give an entry its description, folder and count, which holds its bytes, which verb adds one.
   *  A host finds its directory by `key` ("files", "skills"), never by the type's slug. */
  directory?: TypeDirectory
  /** The attribute naming a record's stored bytes (`display.content`): the record has a download
   *  (GET /v1/…/records/{id}/content — Files entries, Field Ops attachments). */
  content?: string
  /** SEN-22: served placement inside the Connections pane. The client never
   * identifies Connection/SyncRun/Automation by record slug. */
  connectionPane?: {
    key: string
    label: string
    order: number
    surface: 'providers' | 'records'
    readOnly: boolean
    detailRole?: 'runs'
    detailFilterAttribute?: string
  }
}

/** A type's directory metadata (`display.directory`, the pack's `TypeSpec.directory`), attribute SLUGS. */
export interface TypeDirectory {
  /** Which directory a host shows it in ("files", "skills"). */
  key: string
  label: string
  /** The attribute an entry's second line is. */
  description?: string
  /** A select attribute: its option's label is the entry's folder. */
  group?: string
  /** The entry's count (a number, or a reverse reference's count) and the server's words for it. */
  count?: string
  countText?: string
  countLabel?: string
  /** The attribute holding the chat thread the record came from. */
  link?: string
  /** The verb that SHARES one of the viewer's own files into the directory (it makes the file's one record);
   *  absent: records are made with the standard create dialogue. */
  add?: string
  /** Owner, 29 Oct: the viewer's own unshared files are listed too, private to them — the folder words for
   *  them and for the shared records ("Mine", "Shared"). Absent: records only. */
  mineLabel?: string
  sharedLabel?: string
}

/** One of the viewer's own files not shared yet (GET /v1/…/daimon/files): private to its maker. */
export interface MyFile {
  file_id: string
  name: string
  content_type: string
  size_bytes: number
  /** The server's words for the size ("12 KB"). */
  size: string
  /** The Files source option id ("upload", "chat", "agent", "artifact"). */
  source: string
  thread_id: string | null
  thread_title: string | null
  created_at: string | null
}

function directoryFrom(display: unknown): TypeDirectory | undefined {
  const d = (display as Record<string, unknown> | null | undefined)?.directory as Record<string, unknown> | undefined
  if (!d || typeof d.key !== 'string' || !d.key) return undefined
  const s = (k: string) => (typeof d[k] === 'string' && d[k] ? (d[k] as string) : undefined)
  return { key: d.key, label: s('label') ?? d.key, description: s('description'), group: s('group'), count: s('count'),
    countText: s('count_text'), countLabel: s('count_label'), link: s('link'), add: s('add'),
    mineLabel: s('mine_label'), sharedLabel: s('shared_label') }
}

/** SEN-1434 cold-read M4 — THE rail accessor. Absent rail means 'records'
 *  (test fixtures, older payloads). Every rail decision (Sidebar split,
 *  landing fallback, dashboard pivot/widget type pickers) goes through this
 *  helper so Connection/SyncRun types never leak into Records surfaces. */
export function railOf(t: TypeInfo): 'records' | 'connections' | 'reference' | 'none' {
  return t.rail ?? 'records'
}

/** A workspace type that belongs on Records surfaces (pickers, landing). */
export function isRecordsRailType(t: TypeInfo): boolean {
  return !t.is_system && railOf(t) === 'records'
}

export function isReferenceRailType(t: TypeInfo): boolean {
  return !t.is_system && railOf(t) === 'reference'
}

export function byRailOrder(a: TypeInfo, b: TypeInfo): number {
  return (a.rail_order ?? 0) - (b.rail_order ?? 0)
}

/** T6/F4 (SEN-1414): one dashboard widget — BINDINGS only (view kind +
 *  roleNames + measure + grid position); rendered at density=block.
 *  SEN-1419: view_kind='text' widgets carry NO type binding (type_id absent)
 *  and keep their content in config.text. */
export interface DashboardWidget {
  widget_id: string
  grid: { x: number; y: number; w: number; h: number }
  type_id?: string | null
  view_kind: string
  title?: string
  /** SEN-1430: `filter` is the compiled reduce FilterClause wire shape;
   *  `filterUi` is the WidgetEditor's FilterBar round-trip state (dropped
   *  when the config would exceed the backend's 8KB bound — see
   *  dashboards/WidgetEditor.tsx, mirroring masterFilter withUi). */
  /** SEN-1431 (ADR-0036): beyond the structural keys named here, the config
   *  also carries the kind's DECLARED option values (config_schema) — open
   *  keys client-side; the backend validates every key against the schema. */
  config: { roleNames?: Record<string, string>; measureQuantity?: string; filter?: unknown; filterUi?: unknown; text?: string } & Record<string, unknown>
  /** A records question beside (or instead of) the kind's reduce (PR 406's
   *  records binding): count and area are record facts, not telemetry. */
  bind?: WidgetBind | null
  /** The card a host draws around the widget (monad/plenum/v1/requests.py
   *  WidgetCard): subtitle, icon, and a badge whose {n} is the drawn count. */
  card?: WidgetCard | null
}
/** monad/plenum/v1/requests.py WidgetCard. */
export interface WidgetCard {
  subtitle?: string | null
  icon?: string | null
  badge?: string | { one: string; other: string } | null
}
/** SEN-1417: the dashboard-level MASTER filter — declared against a pivot
 *  type and composed (AND) onto every widget client-side at request-build
 *  time. `tree` is the compiled reduce FilterClause wire shape plus one
 *  root-level `ui` key (FilterBar round-trip state — see
 *  dashboards/masterFilter.ts stripUi/withUi). Bounded to 8KB by the backend. */

export interface MasterFilter {
  pivot_type_id: string
  /** SEN-1763: null and {} both mean "no conditions" — a scoped portfolio
   *  binding (drawable with zero chips), mirroring layouts.py MasterFilter. */
  tree: Record<string, unknown> | null
}
export interface DashboardInfo {
  layout_id: string
  title: string
  widgets: DashboardWidget[]
  created_at: string | null
  master_filter: MasterFilter | null
  /** SEN-1720: the dashboard's ONE time window — preset (relative, resolved
   *  at read time) or fixed — or null for the engine default. */
  master_period: MasterPeriod | null
  /** SEN-433 (§3.4): the page layout — 'grid' (the canvas, default) or 'rows'
   *  (§4.1) — the dashboard's description, and whether it is the tenant's
   *  default (read-only: a pack's install sets it). */
  layout?: 'grid' | 'rows'
  description?: string | null
  is_default?: boolean
}
/** The list endpoint returns SUMMARIES only (H2 — no widget arrays on the
 *  every-app-mount sidebar fetch). `is_default` is how a host finds the
 *  tenant's default dashboard from the list it already reads (SEN-433). */
export interface DashboardSummary {
  layout_id: string
  title: string
  created_at: string | null
  description?: string | null
  layout?: 'grid' | 'rows'
  is_default?: boolean
}

/** A truthful, discoverable affordance as GET /tenants/{slug} advertises it
 *  (monad/api/tenants.py tenant_*_affordance): the client calls what the payload
 *  names, never a hard-coded route. */
export interface Affordance {
  supported: boolean
  method?: string
  endpoint?: string
  body?: Record<string, unknown>
  requires?: string
  fields?: string[]
}

/** Tenant metadata — locale is the BCP-47 formatting locale (e.g. "da-DK").
 *  SEN-1330: carries the settings + affordances + readiness summary the
 *  backend already returns (they were dropped before). */
export interface TenantInfo {
  tenant_id: string
  slug: string
  name: string
  locale: string
  created_at: string
  settings?: Record<string, unknown>
  configure?: Affordance
  /** SEN-1790: settings are CELLS on the tenant's self-record (settings-as-
   *  records, SEN-1338) — this points at that record so the discoverable
   *  editor is the record page's own metadata-driven attribute editors.
   *  Null for a tenant without a self-record. */
  settings_record?: { type_id: string; record_id: string } | null
  provision?: Affordance
  delete?: Affordance
  deletable?: boolean
  readiness?: { ready: boolean; endpoint: string }
}

/** One agent run on a record (monad/agent/record_runs.py runs_for_record). */
export interface AgentRun {
  run_id: string
  status: 'streaming' | 'complete' | 'stopped' | 'failed'
  error_code: string | null
  comment_id: string | null
  answer_comment_id: string | null
  /** the agent's answer, private to the mentioner until they post it (null once posted) */
  draft?: string | null
  started_at: string
  finished_at: string | null
  tool_calls: Array<{ tool: string; ok: boolean; label?: string | null; summary?: string | null; duration_ms?: number | null }>
  confirmations: AgentCard[]
}

/** A confirmation card (monad/agent/ui_cards.json `confirmation`), rendered by approvals/ChangeCard. */
export interface AgentCard {
  confirmation_id: string
  title: string
  description?: string | null
  values: Array<{ name: string; label: string; value: string }>
  changes: Array<{ record: string; field: string; old?: string | null; value?: string | null; kind?: string | null;
                   config?: Record<string, unknown> | null; old_value?: unknown; new_value?: unknown }>
  creates: number
  affected: Array<{ record_id: string; kenning: string }>
  on_apply: 'apply' | 'submit_for_review'
  status: string
  outcome?: { code?: string; message?: string } | null
}

/** One Google Places suggestion (`GET /v1/…/places/autocomplete`). */
export interface PlacePrediction {
  place_id: string
  description: string
  main_text: string
  secondary_text: string
}

/** A resolved place (`GET /v1/…/places/{place_id}/details`). `location` and
 *  `opening_periods` are already CELL values — the server shapes them for the
 *  `location` and `opening-hours` codecs, so the client never re-assembles an
 *  address or re-encodes hours. The flat parts are Sentinel's shape, kept for
 *  display. */
export interface PlaceDetails {
  place_id: string
  name: string
  formatted_address: string
  latitude: number
  longitude: number
  location: Record<string, unknown>
  opening_periods: { periods: unknown[]; weekday_text?: string[] } | null
  [k: string]: unknown
}

export const monadClient = {
  /** Fetch tenant metadata including locale (GET /tenants/{slug}).
   *  Used at app boot to seed the ONE seam for number formatting. */
  async getTenant(slug: string): Promise<TenantInfo> {
    return getJson(`/tenants/${slug}`) as Promise<TenantInfo>
  },

  /** GET /tenants/{slug}/readiness — the read-only provisioning manifest (SEN-1330). */
  async getReadiness(slug: string): Promise<ReadinessManifest> {
    return getJson(`/tenants/${slug}/readiness`) as Promise<ReadinessManifest>
  },

  /** Invoke an advertised affordance (method + endpoint + body from the payload). */
  async callAffordance(a: Affordance): Promise<unknown> {
    if (!a.supported || !a.endpoint) throw new HttpError(400, 'affordance not supported')
    const res = await authedFetch(a.endpoint, {
      method: a.method ?? 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(a.body ?? {}),
    })
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { detail?: unknown }
      throw new HttpError(res.status, typeof body.detail === 'string' ? body.detail : `${res.status} ${res.statusText}`, body.detail)
    }
    return res.json().catch(() => null)
  },

  /** DEV-ONLY tenant-switcher source (GET /dev/tenants). Returns [] if the
   *  endpoint is gated off (404 in prod) — so the switcher silently hides.
   *  Each row carries an admin member_id to use as X-Member-Id for that tenant.
   *  TEMPORARY: removed when real login (SEN-685) lands. */
  async listDevTenants(): Promise<Array<{ slug: string; name: string; member_id: string }>> {
    try {
      const res = await authedFetch('/dev/tenants', { headers: { 'Content-Type': 'application/json' } })
      if (!res.ok) return []
      return (await res.json()) as Array<{ slug: string; name: string; member_id: string }>
    } catch {
      return []
    }
  },

  /** List the entity types in a tenant (GET /v1/tenants/{slug}/objects). */
  async listTypes(slug: string, opts: { lastWrite?: boolean } = {}): Promise<TypeInfo[]> {
    const raw = await fetchV1<Array<Record<string, unknown>>>(`/v1/tenants/${slug}/objects${opts.lastWrite ? '?last_write=true' : ''}`)
    return raw.map((t) => {
      const name = String(t.name ?? t.slug)
      // plural_noun comes through the passthrough() schema at runtime when the
      // backend sends it. Fall back to name + "s" (simple pluralisation).
      const pluralName = t.plural_noun ? String(t.plural_noun) : `${name}s`
      // SEN-1434: unknown/absent rail collapses to 'records' (fail-safe —
      // never hide a type on a value the client does not recognise).
      const rail = t.rail === 'connections' || t.rail === 'reference' || t.rail === 'none' ? t.rail : 'records'
      const rail_order = typeof t.rail_order === 'number' && Number.isInteger(t.rail_order) && t.rail_order >= 0
        ? t.rail_order : 0
      const pane = t.connection_pane as Record<string, unknown> | undefined
      const connectionPane = pane
        ? { key: String(pane.key), label: String(pane.label), order: Number(pane.order), surface: pane.surface as 'providers' | 'records', readOnly: Boolean(pane.read_only),
            ...(pane.detail_role === 'runs' && typeof pane.detail_filter_attribute === 'string'
              ? { detailRole: 'runs' as const, detailFilterAttribute: pane.detail_filter_attribute } : {}) }
        : undefined
      return {
        type_id: String(t.type_id), slug: String(t.slug), name, pluralName,
        icon: t.icon ? String(t.icon) : null,
        is_system: Boolean(t.is_system),
        rail,
        rail_order,
        connectionPane,
        ...(directoryFrom(t.display) ? { directory: directoryFrom(t.display) } : {}),
        ...(typeof (t.display as Record<string, unknown> | null | undefined)?.content === 'string'
          ? { content: (t.display as Record<string, string>).content } : {}),
        ...(typeof t.record_count === 'number' ? { recordCount: t.record_count } : {}),
        ...(typeof t.last_write_at === 'string' || t.last_write_at === null ? { lastWriteAt: t.last_write_at as string | null } : {}),
      }
    })
  },
  // ── Dashboards (T6/F4, SEN-1414) — cross-type widget grids ────────────────
  async listDashboards(slug: string): Promise<DashboardSummary[]> {
    return fetchV1<DashboardSummary[]>(`/v1/tenants/${slug}/dashboards`)
  },
  async getDashboard(slug: string, layoutId: string): Promise<DashboardInfo> {
    return fetchV1<DashboardInfo>(`/v1/tenants/${slug}/dashboards/${layoutId}`)
  },
  async createDashboard(slug: string, title: string, widgets: DashboardWidget[] = []): Promise<DashboardInfo> {
    return fetchV1<DashboardInfo>(`/v1/tenants/${slug}/dashboards`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, widgets }),
    })
  },
  /** PATCH is presence-based (backend exclude_unset): omit `master_filter` to
   *  leave it untouched; send an EXPLICIT null to clear it. v1 transport (SEN-1420). */
  async patchDashboard(slug: string, layoutId: string, patch: { title?: string; description?: string | null; layout?: 'grid' | 'rows'; widgets?: DashboardWidget[]; master_filter?: MasterFilter | null; master_period?: MasterPeriod | null }): Promise<void> {
    await fetchV1<{ layout_id: string }>(`/v1/tenants/${slug}/dashboards/${layoutId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  },
  async deleteDashboard(slug: string, layoutId: string): Promise<void> {
    await fetchV1<{ layout_id: string }>(`/v1/tenants/${slug}/dashboards/${layoutId}`, { method: 'DELETE' })
  },

  async getType(slug: string, typeId: string) {
    return TypeResponse.parse(await fetchV1(`/v1/tenants/${slug}/objects/${typeId}`))
  },
  /** `calculated`: attribute ids/slugs of calculated columns to resolve server-side
   *  (read-time Nous resolution — SEN-1395). Omit for a plain cell read; '*' = all.
   *
   *  SEN-1731 cold-read round 3 major #1: `period` mirrors getValues's
   *  — sent ONLY when the caller supplies one, as query params (this is a GET).
   *  RecordPage has no strip/chip of its own, so it always passes
   *  `defaultPeriod()`; the table's CSV export passes the table's own resolved
   *  period so the export agrees with what's on screen. Omitting `period`
   *  entirely keeps the endpoint's own trailing-365-day default unchanged. */
  async getRecord(
    slug: string,
    typeId: string,
    recordId: string,
    calculated?: string[] | '*',
    period?: { period_start: string; period_end: string },
  ) {
    const params = new URLSearchParams()
    if (calculated === '*') params.set('calculated', '*')
    else if (calculated && calculated.length > 0) params.set('calculated', calculated.join(','))
    if (period) {
      params.set('period_start', period.period_start)
      params.set('period_end', period.period_end)
    }
    const q = params.toString() ? `?${params.toString()}` : ''
    return RecordResponse.parse(await fetchV1(`/v1/tenants/${slug}/objects/${typeId}/records/${recordId}${q}`))
  },
  /** Create a blank record of a type (SEN-710 quick-create). The server sets
   *  the author from the authenticated principal; the values map is empty. */
  async createRecord(slug: string, typeId: string): Promise<{ record_id: string }> {
    return fetchV1<{ record_id: string }>(`/v1/tenants/${slug}/objects/${typeId}/records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: {} }),
    })
  },
  async getAttributes(slug: string, typeId: string) {
    return AttributesResponse.parse(await fetchV1(`/v1/tenants/${slug}/objects/${typeId}/attributes`))
  },
  async getViews(slug: string, typeId: string) {
    const data = await fetchV1<{ views: unknown[] }>(`/v1/tenants/${slug}/objects/${typeId}/views`)
    return ViewsResponse.parse(data.views)
  },
  /** One value on one record: a one-key PATCH of the record's values map
   *  (`null` clears). Resolves with the record as it now stands. */
  async setValue(
    slug: string,
    typeId: string,
    attributeId: string,
    recordId: string,
    value: unknown,
  ): Promise<{ record_id: string }> {
    return fetchV1<{ record_id: string }>(
      `/v1/tenants/${slug}/objects/${typeId}/records/${recordId}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ values: { [attributeId]: value } }) },
    )
  },
  /** Archive (soft-delete) a record. */
  async archiveRecord(slug: string, typeId: string, recordId: string): Promise<void> {
    await fetchV1(`/v1/tenants/${slug}/objects/${typeId}/records/${recordId}`, { method: 'DELETE' })
  },
  /** SEN-75: values for record ids, one attribute per table-column call or
   *  several for the existing calculated batch. Absent cells mean missing or
   *  refused.
   *
   *  SEN-1731 ("the strip IS the declaration"): `period` is the strip's
   *  resolved window, sent ONLY when the caller (a table whose visible
   *  columns include a windowed calculated attribute) supplies one — an
   *  absent `period` sends neither field, so the endpoint's own trailing-365
   *  day default is unchanged for every other caller. */
  /** The batch endpoint SPEAKS about its own gaps (SEN-1719/1729/1731:
   *  `calculated_failed`, `calculated_unresolved`, engine/period notes ride
   *  meta.warnings) — return them WITH the values so the table can show them
   *  instead of collapsing every gap into an em-dash (SEN-1784 known-gap). */
  async getValues<T = unknown>(
    slug: string,
    typeId: string,
    recordIds: string[],
    attributes: string[],
    period?: { period_start: string; period_end: string },
    /** SEN-443: stream the answer per column — called with the answer as far
     *  as it has arrived after every line (the stored columns first, then
     *  each completed group of calculated columns). The read then goes on
     *  its own, never through the batch (a batch buffers an item whole). */
    onPartial?: (answer: ValuesAnswer<T>) => void,
  ): Promise<ValuesAnswer<T>> {
    const path = `/v1/tenants/${slug}/objects/${typeId}/records/values`
    const init: RequestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ record_ids: recordIds.slice(0, 1000), attributes, ...(period ?? {}) }),
    }
    if (!onPartial) {
      const { data, meta } = await readV1WithMeta<{ values: Record<string, Record<string, T>> }>(path, init)
      return { values: data.values, warnings: calcWarnings((meta as { warnings?: unknown }).warnings),
        complete: [...attributes], done: true }
    }
    const answer: ValuesAnswer<T> & { complete: string[] } = { values: {}, warnings: [], complete: [], done: false }
    let failure: PlenumApiError | null = null
    const whole = await streamV1Lines(path, init, (raw) => {
      const line = raw as RecordValuesChunk
      for (const [rid, cells] of Object.entries(line.values ?? {})) {
        answer.values[rid] = { ...(answer.values[rid] ?? {}), ...(cells as Record<string, T>) }
      }
      answer.warnings = [...answer.warnings, ...calcWarnings(line.warnings)]
      answer.complete = [...answer.complete, ...line.columns]
      if (line.error) {
        const e = line.error as { owner?: string; code?: string; message?: string; recoverability?: string;
          details?: Record<string, unknown> }
        failure = new PlenumApiError(e.owner ?? 'monad', e.code ?? 'internal', e.message ?? 'The answer could not be completed.',
          e.recoverability ?? 'retry', 200, e.details ?? {})
        return
      }
      if (line.done) answer.complete = [...attributes]
      answer.done = !!line.done
      onPartial({ ...answer, values: { ...answer.values } })
    })
    if (whole) {
      // not a stream: an error envelope (thrown), or a whole JSON answer
      const { data, meta } = unwrapV1WithMeta<{ values: Record<string, Record<string, T>> }>(whole.status, whole.json)
      return { values: data.values, warnings: calcWarnings((meta as { warnings?: unknown }).warnings),
        complete: [...attributes], done: true }
    }
    if (failure) throw failure
    return { ...answer, complete: [...attributes], done: true }
  },
  async getMrl(
    slug: string,
    typeId: string,
    limit: number,
    offset: number,
    filter?: FilterExpr,
    sort?: SortKeyWire[],
    /** R1 (Task 9 fix round 1): a slug-based (reduce-wire) filter clause —
     *  sent as the backend's `filter` field (resolved server-side), NEVER
     *  alongside `filter_resolved` (`POST .../records/query` refuses both at
     *  once — monad/api/v1/records.py). Wins over `filter` when both are
     *  given: a widget's fixed config.filter is not the same state as the
     *  FilterBar's user-edited tree, and the two must never collide. */
    filterWire?: FilterClause,
    /** SEN-1784: the window a CALCULATED filter clause is compared over —
     *  the same strip period the calculated COLUMNS use, so the filter and
     *  the visible values agree. Ignored server-side for ordinary filters. */
    period?: { period_start: string; period_end: string },
  ) {
    const body: Record<string, unknown> = { limit, offset }
    if (filterWire !== undefined) body.filter = filterWire
    else if (filter) body.filter_resolved = filter
    if (sort && sort.length > 0) body.sorts_resolved = sort
    if (period) { body.period_start = period.period_start; body.period_end = period.period_end }
    const { data, meta } = await readV1WithMeta<Array<{ record_id: string; kenning?: string }>>(
      `/v1/tenants/${slug}/objects/${typeId}/records/query`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    )
    return MrlResponse.parse({
      result_snapshot_id: meta.source_snapshot,
      total_count: (meta.pagination as { total?: number } | undefined)?.total ?? data.length,
      record_ids: data.map((r) => String(r.record_id)),
      // Omit records whose kenning is absent — never coerce to the string 'undefined'
      // (never-show-uuid: a missing label must fall through to fallback rendering).
      record_kennings: Object.fromEntries(
        data.filter((r) => r.kenning !== undefined).map((r) => [String(r.record_id), String(r.kenning)]),
      ),
    })
  },
  /** Sites on Poiesis (2026-10-21): ONE page of a collection — records/query
   *  with `limit`/`offset`, the view's filter and sort, and the page's STORED
   *  cells inline (`projection`, keyed by the token as sent), so a screen
   *  costs one read per page, never the whole set. `period` rides only when
   *  the caller's filter or sort needs a calculated value (the server ignores
   *  it otherwise). Warnings (a calculated sort's notes) come back with it. */
  async getPage(
    slug: string,
    typeId: string,
    page: {
      limit: number
      offset: number
      filterWire?: FilterClause
      filter?: FilterExpr
      sort?: SortKeyWire[]
      projection?: string[]
      period?: { period_start: string; period_end: string }
      /** Each row's chip (icon, colour) inline, in the same read. */
      chips?: boolean
    },
  ): Promise<{
    rows: Array<{ record_id: string; kenning: string; values: Record<string, unknown>; icon?: string | null; color?: string | null }>
    total: number
    warnings: CalcBatchWarning[]
  }> {
    const body: Record<string, unknown> = { limit: page.limit, offset: page.offset }
    // both may ride together: the server ANDs a wire clause with a resolved filter
    if (page.filterWire !== undefined) body.filter = page.filterWire
    if (page.filter) body.filter_resolved = page.filter
    if (page.sort && page.sort.length > 0) body.sorts_resolved = page.sort
    if (page.projection && page.projection.length > 0) body.projection = page.projection
    if (page.period) { body.period_start = page.period.period_start; body.period_end = page.period.period_end }
    if (page.chips) body.chips = true
    const { data, meta } = await readV1WithMeta<Array<{ record_id: string; kenning?: string; values?: Record<string, unknown> | null; icon?: string | null; color?: string | null }>>(
      `/v1/tenants/${slug}/objects/${typeId}/records/query`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    )
    const raw = (meta as { warnings?: unknown }).warnings
    return {
      rows: data.map((r) => ({
        record_id: String(r.record_id), kenning: r.kenning ?? '', values: r.values ?? {},
        ...(page.chips ? { icon: r.icon ?? null, color: r.color ?? null } : {}),
      })),
      total: (meta.pagination as { total?: number } | undefined)?.total ?? data.length,
      warnings: Array.isArray(raw)
        ? raw.filter((w): w is CalcBatchWarning => typeof w === 'object' && w !== null
            && typeof (w as CalcBatchWarning).kind === 'string' && typeof (w as CalcBatchWarning).message === 'string')
        : [],
    }
  },
  /** A described SET of records as one file, resolved by the engine in ONE read: records/query
   *  with `Accept: text/csv` (the server pages the whole set itself, its own bound applies) — the
   *  set's filter (a selection: the question AND its exclusions, or the picked ids), the table's
   *  order and its columns as `projection`. Never a client loop over ids. */
  async exportRecords(
    slug: string,
    typeId: string,
    set: {
      filter?: FilterClause
      filter_resolved?: FilterExpr
      sort?: SortKeyWire[]
      projection: string[]
      period?: { period_start: string; period_end: string }
    },
  ): Promise<string> {
    const body: Record<string, unknown> = { limit: 1, offset: 0, projection: set.projection }
    if (set.filter !== undefined) body.filter = set.filter
    if (set.filter_resolved) body.filter_resolved = set.filter_resolved
    if (set.sort && set.sort.length > 0) body.sorts_resolved = set.sort
    if (set.period) { body.period_start = set.period.period_start; body.period_end = set.period.period_end }
    const res = await authedFetch(`/v1/tenants/${slug}/objects/${typeId}/records/query`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'text/csv' }, body: JSON.stringify(body),
    })
    if (!res.ok) unwrapV1(res.status, await res.json().catch(() => null))
    return res.text()
  },
  /** Fetch the activity timeline for a record.
   *  GET /v1/tenants/{slug}/records/{recordId}/timeline */
  async getTimeline(slug: string, recordId: string) {
    return TimelineResponse.parse(await fetchV1(`/v1/tenants/${slug}/records/${recordId}/timeline`))
  },
  /** Record counts per distinct value of `groupBy` (slug / dotted paths) —
   *  records/query `group_by`: the same filter grammar and row scope as the
   *  list it heads, ONE SQL GROUP BY. A reference key comes with its kenning. */
  async groupRecords(
    slug: string,
    typeId: string,
    groupBy: string[],
    filter?: FilterClause,
    /** How many groups to read (default 200: a list's group headers). */
    limit = 200,
  ): Promise<RecordGroup[]> {
    return monadClient.askRecords(slug, typeId, { group_by: groupBy, limit, ...(filter !== undefined ? { filter } : {}) })
  },
  /** One page of records/query `group_by` groups with the group total (`meta.pagination.total`):
   *  the wire filter and a resolved filter apply together; `chips` adds each reference key's icon. */
  async groupPage(
    slug: string,
    typeId: string,
    q: { groupBy: string[]; filterWire?: FilterClause; filter?: FilterExpr; limit: number; offset: number; chips?: boolean },
  ): Promise<{ groups: RecordGroup[]; total: number }> {
    const body: Record<string, unknown> = { group_by: q.groupBy, limit: q.limit, offset: q.offset }
    if (q.filterWire !== undefined) body.filter = q.filterWire
    if (q.filter) body.filter_resolved = q.filter
    if (q.chips) body.chips = true
    const { data, meta } = await readV1WithMeta<RecordGroup[]>(`/v1/tenants/${slug}/objects/${typeId}/records/query`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
    return { groups: data, total: (meta.pagination as { total?: number } | undefined)?.total ?? data.length }
  },
  /** A widget's records question (`bind.records`, PR 406's records binding):
   *  records/query with `group_by` and/or `aggregates` — one GROUP BY, or one
   *  total RecordGroup when there is no group_by. Through the read coalescer,
   *  so it rides the same /batch as the page's other questions. `object` is a
   *  slug or an id. */
  async askRecords(slug: string, object: string, query: RecordsQuestion): Promise<RecordGroup[]> {
    return readV1<RecordGroup[]>(`/v1/tenants/${slug}/objects/${object}/records/query`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(query),
    })
  },
  /** Records of a type with `projection` cells inline (records/query, one read). */
  async queryRecords(
    slug: string,
    typeId: string,
    body: { filter?: FilterClause; projection?: string[]; limit?: number; sorts?: Array<{ field: string; direction?: 'asc' | 'desc' }>
      /** Each row also says when the record was last written (`last_write_at`): a directory's "Last updated". */
      last_write?: boolean },
  ): Promise<Array<{ record_id: string; kenning: string; values?: Record<string, unknown>; last_write_at?: string | null }>> {
    return readV1(`/v1/tenants/${slug}/objects/${typeId}/records/query`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ limit: 20, ...body }),
    })
  },
  /** The viewer's own files not shared yet (Files' "Mine"; owner, 29 Oct): private to their maker. */
  async listMyFiles(slug: string): Promise<MyFile[]> {
    return fetchV1<MyFile[]>(`/v1/tenants/${encodeURIComponent(slug)}/daimon/files`)
  },
  /** The type's record-page layouts (persisted, else the server's fabricated
   *  default: one value-widget per readable attribute). */
  async getLayouts(slug: string, typeId: string): Promise<RecordPageLayout[]> {
    return fetchV1<RecordPageLayout[]>(`/v1/tenants/${slug}/objects/${typeId}/layouts`)
  },
  /** A record's comment thread (oldest first). */
  async listComments(slug: string, typeId: string, recordId: string): Promise<CommentData[]> {
    const data = await fetchV1<{ comments: CommentData[] }>(
      `/v1/tenants/${slug}/objects/${typeId}/records/${recordId}/comments`)
    return data.comments
  },
  /** Add a comment. `mentions` are typed refs ({type: 'person', id}); each
   *  mentioned person is notified (monad/notifications.py notify_mentions). */
  async addComment(
    slug: string,
    typeId: string,
    recordId: string,
    body: { body: string; parent_id?: string | null; mentions?: CommentMention[] },
  ): Promise<CommentData> {
    return fetchV1<CommentData>(`/v1/tenants/${slug}/objects/${typeId}/records/${recordId}/comments`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
  },
  /** @agent in a record's comments: run the tenant's agent for the person's own comment (202). */
  async startAgentRun(slug: string, typeId: string, recordId: string, commentId: string): Promise<{ run_id: string }> {
    return fetchV1(`/v1/tenants/${slug}/objects/${typeId}/records/${recordId}/comments/agent-runs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment_id: commentId }),
    })
  },
  /** The mentioner publishes their private draft: a reply by the agent, published by them (once). */
  async postAgentReply(slug: string, typeId: string, recordId: string, runId: string): Promise<{ comment_id: string }> {
    return fetchV1(`/v1/tenants/${slug}/objects/${typeId}/records/${recordId}/comments/agent-runs/${encodeURIComponent(runId)}/post`,
      { method: 'POST' })
  },
  /** The caller's own agent runs on a record: working / done, their steps and confirmation cards. */
  async listAgentRuns(slug: string, typeId: string, recordId: string): Promise<AgentRun[]> {
    const data = await fetchV1<{ runs: AgentRun[] }>(
      `/v1/tenants/${slug}/objects/${typeId}/records/${recordId}/comments/agent-runs`)
    return data.runs
  },
  /** The person applies a change the agent previewed: NomOS replays it as them, under their own rights. */
  async applyConfirmation(slug: string, confirmationId: string): Promise<AgentCard> {
    return fetchV1(`/v1/tenants/${slug}/daimon/confirmations/${encodeURIComponent(confirmationId)}/apply`, { method: 'POST' })
  },
  /** The person dismisses it: nothing changes. */
  async dismissConfirmation(slug: string, confirmationId: string): Promise<AgentCard> {
    return fetchV1(`/v1/tenants/${slug}/daimon/confirmations/${encodeURIComponent(confirmationId)}/dismiss`, { method: 'POST' })
  },
  async resolveComment(slug: string, commentId: string): Promise<CommentData> {
    return fetchV1<CommentData>(`/v1/tenants/${slug}/comments/${commentId}/resolve`, { method: 'POST' })
  },
  async undoCell(slug: string, cellId: string) {
    return fetchV1<{ cell_id: string; undoes_cell_id: string }>(
      `/v1/tenants/${slug}/cells/${cellId}/undo`, { method: 'POST' })
  },
  async invokeDaimon(slug: string, body: DaimonInvoke, signal?: AbortSignal): Promise<DaimonOutcome> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/daimon/invoke`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal,
    })
  },
  async getDaimonCapability(slug: string): Promise<DaimonCapability> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/daimon/capability`)
  },
  async getDaimonInvocations(slug: string): Promise<DaimonInvocation[]> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/daimon/invocations`)
  },
  async getDaimonInvocation(slug: string, id: string): Promise<DaimonInvocation> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/daimon/invocations/${encodeURIComponent(id)}`)
  },
  async getProposedChange(slug: string, id: string): Promise<ProposedChangeData> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/proposed-changes/${encodeURIComponent(id)}`)
  },
  /** The one Approvals list (change-sets spec §6): newest first, without the
   *  writes — one change's detail reads them with getProposedChange. */
  async queryProposedChanges(
    slug: string, body: { status?: string; verb_slug?: string; limit?: number; offset?: number
      /** Only changes whose recorded parameters CONTAIN these keys/values. */
      parameters?: Record<string, unknown> } = {},
  ): Promise<ProposedChangeData[]> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/proposed-changes/query`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
  },
  async declineProposedChange(slug: string, id: string): Promise<{ proposed_change_id: string; status: string }> {
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/proposed-changes/${encodeURIComponent(id)}/decline`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    })
  },

  /** List synced verbs for a tenant.
   *  GET /v1/tenants/{slug}/actions */
  async getVerbs(
    slug: string,
  ): Promise<Array<VerbSummary>> {
    return fetchV1<Array<VerbSummary>>(`/v1/tenants/${slug}/actions`)
  },

  async invokeVerb(
    slug: string,
    verbSlug: string,
    target: Record<string, unknown>,
  ): Promise<{ proposed_change_id: string | null; status: string }> {
    const data = await fetchV1<{ proposed_change_id: string | null; status: string }>(
      `/v1/tenants/${slug}/actions/${verbSlug}/invoke`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parameters: target }) },
    )
    return { proposed_change_id: data.proposed_change_id ?? null, status: data.status }
  },

  /** Invoke a verb in a mode: `apply` (applied at once or queued for a person, as the pipeline decides),
   *  `dry_run` (computed, never applied) or `propose` (always queued). */
  async invokeVerbMode(
    slug: string,
    verbSlug: string,
    parameters: Record<string, unknown>,
    mode: 'apply' | 'dry_run' | 'propose' = 'apply',
  ): Promise<VerbInvocation> {
    const data = await fetchV1<VerbInvocation>(
      `/v1/tenants/${slug}/actions/${verbSlug}/invoke`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parameters, mode }) },
    )
    return { ...data, proposed_change_id: data.proposed_change_id ?? null }
  },

  async approveProposedChange(
    slug: string,
    proposedChangeId: string,
  ): Promise<{ proposed_change_id: string; status: string }> {
    const data = await fetchV1<{ proposed_change_id: string; status: string }>(
      `/v1/tenants/${slug}/proposed-changes/${proposedChangeId}/approve`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) },
    )
    return { proposed_change_id: data.proposed_change_id, status: data.status }
  },

  /** Batch chip resolution — kenning + conventional icon/colour siblings.
   *  POST /v1/tenants/{slug}/records/chips
   *  Returns {chips: {record_id: {kenning, icon, color}}} for every requested id.
   *  One chip seam, every surface (design.md CEO round 1, Rule 3). */
  async getChips(
    slug: string,
    recordIds: string[],
    references: import('./schemas').ChipReferenceLookup[] = [],
  ): Promise<ChipsResponse> {
    return ChipsResponse.parse(await readV1(`/v1/tenants/${slug}/records/chips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ record_ids: recordIds, references }),
    }))
  },

  async saveView(
    slug: string,
    typeId: string,
    viewId: string,
    patch: ViewPatch,
  ): Promise<{ view_id: string }> {
    // Generic view-write (the narrow waist). X-Member-Id is the dev principal
    // shim; the backend 403s a cross-tenant member and 401s a missing/unknown id.
    return fetchV1<{ view_id: string }>(`/v1/tenants/${slug}/objects/${typeId}/views/${viewId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
  },

  // ── Builder write surface (SEN-735) ──────────────────────────────────────────

  async createType(slug: string, body: TypeCreateBody): Promise<{ type_id: string }> {
    return fetchV1<{ type_id: string }>(`/v1/tenants/${slug}/objects`, v1Write('POST', body))
  },

  async patchType(slug: string, typeId: string, body: TypeUpdateBody): Promise<Record<string, unknown>> {
    return fetchV1<Record<string, unknown>>(`/v1/tenants/${slug}/objects/${typeId}`, v1Write('PATCH', body))
  },

  async createAttribute(slug: string, typeId: string, body: AttributeCreateBody): Promise<{ attribute_id: string }> {
    return fetchV1<{ attribute_id: string }>(`/v1/tenants/${slug}/objects/${typeId}/attributes`, v1Write('POST', body))
  },

  async patchAttribute(slug: string, typeId: string, attributeId: string, body: AttributeUpdateBody): Promise<Record<string, unknown>> {
    return fetchV1<Record<string, unknown>>(`/v1/tenants/${slug}/objects/${typeId}/attributes/${attributeId}`, v1Write('PATCH', body))
  },

  async reorderAttributes(slug: string, typeId: string, orderedIds: string[]): Promise<{ ok: boolean; ordered_ids: string[] }> {
    await fetchV1<unknown>(`/v1/tenants/${slug}/objects/${typeId}/attributes`, v1Write('PATCH', { ordered_ids: orderedIds }))
    return { ok: true, ordered_ids: orderedIds }
  },

  /** Is the Google Places lookup available on this server (the key is the server's)? */
  async getPlacesConfig(slug: string): Promise<{ configured: boolean }> {
    return fetchV1<{ configured: boolean }>(`/v1/tenants/${encodeURIComponent(slug)}/places/config`)
  },

  /** Suggestions for a search text. `sessionToken` groups one search session's
   *  calls for Google billing — one token per session, ended by a details call. */
  async autocompletePlaces(
    slug: string, query: string, sessionToken: string,
  ): Promise<{ predictions: PlacePrediction[]; configured: boolean }> {
    const q = new URLSearchParams({ query, session_token: sessionToken })
    return fetchV1(`/v1/tenants/${encodeURIComponent(slug)}/places/autocomplete?${q}`)
  },

  /** Resolve a picked suggestion; closes the search session `sessionToken` opened. */
  async getPlaceDetails(slug: string, placeId: string, sessionToken: string): Promise<PlaceDetails> {
    const q = new URLSearchParams({ session_token: sessionToken })
    return fetchV1<PlaceDetails>(
      `/v1/tenants/${encodeURIComponent(slug)}/places/${encodeURIComponent(placeId)}/details?${q}`,
    )
  },

  async listSubtypes(slug: string): Promise<Subtype[]> {
    // SEN-219: the offerings are the ontology's `kinds` (one metadata read).
    const ontology = await fetchV1<{ kinds: unknown[] }>(`/v1/tenants/${slug}/ontology`)
    return SubtypesResponse.parse(ontology.kinds)
  },

  /** Deactivate = set the deactivated_at cell (bare ISO string). */
  async deactivateSubtypeRecord(
    slug: string,
    typeId: string,
    deactivatedAtAttrId: string,
    recordId: string,
    iso: string,
  ): Promise<void> {
    await monadClient.setValue(slug, typeId, deactivatedAtAttrId, recordId, iso)   // bare ISO string
  },
}
